import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Spinner from '@/components/ui/Spinner';

/**
 * AdminGuard wraps admin-only route groups.
 *
 * - While session is being validated: shows a centered loading spinner.
 * - If not authenticated: redirects to /login.
 * - If authenticated but not admin: renders a 403 page.
 * - If admin: renders the nested routes via <Outlet />.
 *
 * Usage (as a layout route wrapper):
 *   <Route element={<AdminGuard />}>
 *     <Route path="/admin/users" element={<AdminUsers />} />
 *   </Route>
 *
 * Usage (as a direct wrapper around a page component):
 *   <AdminGuard><AdminInvitations /></AdminGuard>
 */
interface AdminGuardProps {
  children?: React.ReactNode;
}

export default function AdminGuard({ children }: AdminGuardProps) {
  const { isLoading, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-base">
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh] gap-4"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: 'rgba(248, 113, 113, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            marginBottom: '8px',
          }}
        >
          403
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '20px',
            color: 'var(--color-text-primary)',
          }}
        >
          Access denied
        </h2>
        <p style={{ fontSize: '14px' }}>Admin role required to view this page.</p>
      </div>
    );
  }

  // When used as a layout route (<Route element={<AdminGuard />}>), render Outlet.
  // When used as a wrapper around a concrete page, render children.
  return children ? <>{children}</> : <Outlet />;
}
